import "./patientSummary.scss";
import { Alert, Space, Typography, theme } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { OutstandingClaimsIndicator } from "./OutstandingClaimsIndicator";
import { type StrictlyBasisPoints, stringFromBasisPoints } from "@vyne-shared/utils/currency";
import { VyneDescriptions } from "@vyne-shared/components/vyneDescriptions/VyneDescriptions";
import { formatDateNumerically } from "@vyne-shared/utils/date";
import type { PmsBalances } from "@vyne-shared/api/VynePayments/responses";

const { Title } = Typography;
const { useToken } = theme;

// Name Area
interface NameAreaProps {
    readonly givenName: string;
    readonly familyName: string;
    readonly isGuarantor?: boolean;
    readonly dateOfBirth?: string;
    readonly pmsBalances?: PmsBalances;
    readonly dependents?: string[];
    readonly hasOutstandingClaims: boolean;
}

export const NameArea = ({
    givenName,
    familyName,
    isGuarantor,
    dateOfBirth,
    pmsBalances,
    dependents,
    hasOutstandingClaims,
}: NameAreaProps) => {
    const name = `${givenName} ${familyName}${isGuarantor !== undefined ? (isGuarantor ? " (Guarantor)" : " (Dependant)") : ""}`;

    return (
        <Space direction="vertical" className="vyne-patient-summary-name-area">
            {pmsBalances && <div className="account-details">Account Details {getBalanceAsOf(pmsBalances)}</div>}
            <Title level={2} aria-label="Patient name">
                {name} <OutstandingClaimsIndicator hasOutstandingClaims={hasOutstandingClaims} />
            </Title>
            {dateOfBirth && (
                <span className="dob" aria-label="Patient date of birth">
                    DOB: {dateOfBirth}
                </span>
            )}
            {dependents && <ViewDependents dependents={dependents} />}
        </Space>
    );
};

const ViewDependents = ({ dependents }: { readonly dependents: string[] }) =>
    dependents.length > 0 ? (
        <details className="dependents">
            <summary>View Dependents</summary>
            <ul>
                {dependents.map((dep) => (
                    <li key={dep}>{dep}</li>
                ))}
            </ul>
        </details>
    ) : (
        <p>No Dependents</p>
    );

function getBalanceAsOf(pmsBalances: PmsBalances) {
    const balanceAsOf = pmsBalances.balanceAsOf;
    return balanceAsOf > 0 ? `(as of ${formatDateNumerically(balanceAsOf)})` : "";
}

// Balance
interface BalanceAreaProps {
    readonly balance?: StrictlyBasisPoints;
    readonly pmsBalances?: PmsBalances;
    readonly hideAlreadyRequested?: boolean;
}

export const BalanceArea = ({ balance, pmsBalances, hideAlreadyRequested = false }: BalanceAreaProps) => {
    const alreadyRequestedItems =
        !hideAlreadyRequested && balance !== undefined
            ? [
                  {
                      label: <AlreadyRequestedLabel />,
                      children: <span aria-label="Already Requested">{formatBalanceField(balance, true)}</span>,
                  },
              ]
            : [];

    const integratedItems = pmsBalances
        ? [
              {
                  label: "Balance",
                  children: <span aria-label="Total Balance">{formatBalanceField(pmsBalances.balanceTotal)}</span>,
              },
              {
                  label: "Insurance Estimate",
                  children: (
                      <span aria-label="Insurance Estimate">
                          {formatBalanceField(pmsBalances.insuranceTotalEstimate)}
                      </span>
                  ),
              },
              {
                  label: "Patient Portion Due",
                  children: (
                      <span aria-label="Patient Portion Due">{formatBalanceField(pmsBalances.balanceEstimated)}</span>
                  ),
              },
          ]
        : [];

    return (
        <VyneDescriptions
            layout="horizontal"
            size="small"
            column={1}
            inCard={true}
            items={[...integratedItems, ...alreadyRequestedItems]}
        />
    );
};

export const AlreadyRequestedLabel = () => {
    const { token } = useToken();

    return (
        <span>
            <ClockCircleOutlined style={{ color: token.colorInfo }} />
            &nbsp; Already Requested
        </span>
    );
};

function formatBalanceField(value: StrictlyBasisPoints | null, treatZeroAsMissing = false) {
    return typeof value === "number" && (value > 0 || !treatZeroAsMissing) ? stringFromBasisPoints(value) : "$ —";
}

// Warning If Not Posted

export const WarnIfNotPosted = ({ paidWithoutWriteBackDate }: { readonly paidWithoutWriteBackDate?: number }) =>
    paidWithoutWriteBackDate ? (
        <Alert
            type="warning"
            showIcon
            message={`The patient's ${formatDateNumerically(paidWithoutWriteBackDate)} payment is not in your practice
                    software yet.`}
        />
    ) : null;
